import React from 'react';
import { useTranslation } from 'react-i18next';
import { Stepper, Step as MuiStep, StepLabel, StepContent } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { actionTakenSubCampaign, chipTypes, statusSubCampaign } from '@/helpers/constants';
import { Step } from '@/types';
import TooltipComponent from '@/components/UI/Tooltip/TooltipComponent';
import UniversalChip from '@/components/UI/UniversalChip/UniversalChip';
import { getStautsIconClassName } from './WorkFlow.utils';
import styles from './StepsComponent.module.scss';

const useStyles = makeStyles(() => ({
  subCRWorkflow: {
    paddingTop: '0',
    paddingLeft: '0',
  },
}));

type SubCRMiniWFComponent = {
  id: string;
  collapsed: boolean;
  directStatuses?: Step[];
}

const SubCRMiniWF: React.FC<SubCRMiniWFComponent> = ({ id, collapsed, directStatuses }) => {
  const { t } = useTranslation();
  const oldVersion = 1;
  const classes = useStyles();
  const checkVersionChip = (stepVersion: string): string => ((Number(stepVersion) < oldVersion) ? `V${stepVersion} -` : '');

  const getActionTakenLabel = (actionTaken: string, statusCode: string): string => {
    if (actionTaken === actionTakenSubCampaign.SENT_TO_APPROVAL && statusCode !== statusSubCampaign.IN_PLANT_VALIDATION) {
      return t('action.IN_PLANT_APPROVAL');
    }

    if (actionTaken === actionTakenSubCampaign.CREATED) {
      return t('action.OPEN');
    }

    return t(`action.${actionTaken}`);
  };

  const getStepLabel = (statusCode: string, completed: boolean): string => {
    if ([statusSubCampaign.ACCEPTED, statusSubCampaign.DENIED].includes(statusCode) && !completed) {
      return `${t('subCampaign.ACCEPTED')}/${t('subCampaign.DENIED')}`;
    }

    return t(`subCampaign.${statusCode}`);
  };

  const getActionTakenSummary = (step: Step): string => {
    if (step?.actionTaken === actionTakenSubCampaign.NO_ACTION_TAKEN && step?.completed) {
      return t('history.NO_ACTION_TAKEN');
    }

    if (step?.current) return step?.currentAssignedUser?.summary || step?.actionTakenBy?.summary;

    return step?.actionTakenBy?.summary;
  };

  return (
    <>
      <Stepper orientation="vertical" nonLinear classes={{ root: classes.subCRWorkflow }}>
        <MuiStep disabled index={-1} />
        {directStatuses?.map((step, index) => (
          <MuiStep
            key={`wf-${id}-${step.statusCode}-${index}`} // eslint-disable-line react/no-array-index-key
            expanded
            active={step.current}
            completed={step.completed}
            index={index}
          >
            <StepLabel>
              <span className={styles.Label}>
                {!collapsed
                  && getStepLabel(step.statusCode, step.completed)}
                {collapsed
                  && step.labelCollapsed}
              </span>
            </StepLabel>
            {!collapsed && (step.current || step.completed)
              && (
                <StepContent>
                  <ul className={styles.User}><li>{getActionTakenSummary(step)}</li></ul>
                  { /* TODO: At this moment BU dont want chips in subCR workflow, but we think that in the future will be required */}
                  <ul style={{ display: 'none' }}>
                    {(step?.actionDate !== '' && step?.actionTaken !== '') && (
                      <TooltipComponent
                        key={`tooltip-${step.statusCode}-${index}`} // eslint-disable-line react/no-array-index-key
                        id={`tooltip-${index}`}
                        title={`${checkVersionChip(step.version)} ${step?.actionDate} - ${getActionTakenLabel(step?.actionTaken, step.statusCode)}`}
                      >
                        <li>
                          <UniversalChip
                            chipType={chipTypes.WITH_FUNCTION}
                            key={`wf-${step.statusCode}-${index}`} // eslint-disable-line react/no-array-index-key
                            id={`wf-${index}`}
                            icon={<i className={getStautsIconClassName(step)} />}
                            text={`${checkVersionChip(step.version)} ${step?.actionDate} -aa ${getActionTakenLabel(step?.actionTaken, step.statusCode)}`}
                            noHighlightChip
                          />
                        </li>
                      </TooltipComponent>
                    )}
                  </ul>
                </StepContent>
              )}
          </MuiStep>
        ))}
      </Stepper>
    </>
  );
};

export default SubCRMiniWF;
